import { HttpErrorResponse } from '@angular/common/http';

/**
 * Attempts to extract a user-friendly error message from an API error response.
 *
 * It looks for an 'error' property in the response body, and if not found, it checks if the body is
 * a non-empty string.
 *
 * If neither is found, it returns null.
 *
 * @param err The error object to extract the message from, typically an HttpErrorResponse.
 * @returns A user-friendly error message if found, or null if no message could be extracted.
 *
 * @author sadatmalik
 */
export function extractApiErrorMessage(err: unknown): string | null {
  if (!(err instanceof HttpErrorResponse)) {
    return null;
  }
  const body = err.error;
  if (typeof body === 'object' && body !== null && 'error' in body) {
    const maybeError = (body as { error: unknown }).error;
    if (typeof maybeError === 'string') {
      const trimmed = maybeError.trim();
      return trimmed.length > 0 ? trimmed : null;
    }
  }
  if (typeof body === 'string' && body.trim().length > 0) {
    return body.trim();
  }
  return null;
}

/**
 * Provides a user-friendly error message for a generic API error. It first attempts to extract a
 * message from the error response using `extractApiErrorMessage`.
 *
 * If that fails, it checks the HTTP status code of the error and returns an appropriate message
 * based on common status codes.
 *
 * If the error is not an HttpErrorResponse or if the status code is unrecognized, it returns a
 * provided fallback message.
 *
 * @param err The error object to describe, typically an HttpErrorResponse.
 * @param fallback A fallback message to use if no specific message can be determined from the error.
 * @returns A user-friendly error message describing the API error.
 *
 * @author sadatmalik
 */
export function describeGenericApiError(err: unknown, fallback: string): string {
  const serverMessage = extractApiErrorMessage(err);
  if (serverMessage) {
    return serverMessage;
  }
  if (!(err instanceof HttpErrorResponse)) {
    return fallback;
  }
  if (err.status === 0) {
    return 'Could not reach the server. Check your connection and try again.';
  }
  if (err.status === 401 || err.status === 403) {
    return 'Not authorized. Try signing in again.';
  }
  if (err.status === 404) {
    return 'The requested resource was not found.';
  }
  if (err.status === 409) {
    return 'Your data is out of date due to a conflict. Refresh and retry.';
  }
  if (err.status === 412) {
    return 'Your data is stale. Refresh and retry.';
  }
  if (err.status >= 500) {
    return 'Server error. Please try again.';
  }
  return `${fallback} (HTTP ${err.status}).`;
}
